import { Box, Link, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import useFormValidation from '../../utils/hooks/useFormValidation'
import ErrorDisplay from './ErrorDisplay'
import FormRenderer from './FormRenderer'
import RecaptchaWrapper from './RecaptchaWrapper'
import { shouldSkipTurnstileOnClient } from '../../services/turnstile'
import SubmitButtonWithStatus from './SubmitButtonWithStatus'

const BLUE_BASE = '#092A48'
const WHITE = '#FFFFFF'

const FormContainer = styled(Box)(({ theme }) => ({ width: '100%' }))
const FormBody = styled('form')(({ theme }) => ({ width: '100%' }))
const FormSuccess = styled(Typography)(({ theme }) => ({ color: BLUE_BASE, marginBottom: theme.spacing(2.5) }))
const GetHelp = styled(Typography)(({ theme }) => ({
  color: 'red',
  margin: theme.spacing(2.5, 0),
  '& a': { color: BLUE_BASE, fontWeight: 'bold', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
}))
const FallbackNotice = styled(Typography)(({ theme }) => ({ marginTop: theme.spacing(1.5), color: '#8A5A00' }))

const shallowEqual = (a = {}, b = {}) => {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false
  }
  return true
}

const CustomForm = ({
  formId,
  formFields = [],
  formHTML = '',
  isModal = false,
  ctaText = 'Submit',
  onSubmit = null,
  thanksMessage: customThanksMessage = '',
  formCSRF,
  initialValues,
  buttonStyles,
  formStyles,
  onValuesChange,
  suppressErrorDisplay = false,
}) => {
  const [formValues, setFormValues] = useState(initialValues || {})
  const [fieldErrors, setFieldErrors] = useState({})
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [thanksMessage, setThanksMessage] = useState('')
  const [verificationBypassed, setVerificationBypassed] = useState(false)
  const fieldRefs = useRef({})
  const turnstileRef = useRef(null)
  const skipTurnstile = useMemo(() => shouldSkipTurnstileOnClient(), [])
  const { validateField, validateAllFields, clearErrors } = useFormValidation(formHTML, formFields, formValues)
  const [recaptchaValue, setRecaptchaValue] = useState('')
  const [resetCaptchaFlag, setResetCaptchaFlag] = useState(false)
  const memoizedFormFields = useMemo(() => formFields, [formFields])

  const initialValuesRef = useRef(initialValues)

  useEffect(() => {
    if (!initialValues || initialValuesRef.current === initialValues) {
      initialValuesRef.current = initialValues
      return
    }
    if (shallowEqual(initialValues, formValues)) {
      initialValuesRef.current = initialValues
      return
    }
    setFormValues(initialValues)
    initialValuesRef.current = initialValues
  }, [initialValues, formValues])

  const handleInputChange = useCallback((e) => {
    const { id, name, type, checked, value } = e.target
    const key = id || name
    const raw = type === 'checkbox' || type === 'radio' ? checked : value
    const val = typeof raw === 'string' ? raw : raw
    setFormValues((prevValues) => {
      const nextValues = { ...prevValues, [key]: val }
      if (typeof onValuesChange === 'function') {
        onValuesChange(nextValues)
      }
      return nextValues
    })
    validateField(key, val)
  }, [onValuesChange, validateField])

  const handleRecaptchaChange = useCallback((value) => {
    setRecaptchaValue(value || '')
  }, [])
  const handleRecaptchaExpire = useCallback(() => {
    setRecaptchaValue('')
  }, [])

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms))
  const tryExecuteTurnstile = useCallback(async () => {
    if (!turnstileRef.current?.execute) return null
    const attempt = async () => {
      try {
        const p = turnstileRef.current.execute()
        const t = new Promise((_, rej) => setTimeout(() => rej(new Error('turnstile_client_timeout')), 14000))
        const token = await Promise.race([p, t]).catch(() => null)
        return token || null
      } catch {
        return null
      }
    }
    let token = await attempt()
    if (!token) {
      try { await turnstileRef.current.reset?.() } catch { /* error */  }
      await sleep(2000)
      token = await attempt()
    }
    return token
  }, [])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    setErrorMessage('')
    setVerificationBypassed(false)
    clearErrors()
    const { hasError, newFieldErrors } = validateAllFields()
    if (hasError) {
      setFieldErrors(newFieldErrors)
      setErrorMessage('Please fix the errors in the form.')
      return
    }
    if (!formCSRF) {
      setErrorMessage('CSRF token is missing.')
      return
    }
    setIsSubmitting(true)
    try {
      let token = recaptchaValue
      let bypassed = false
      if (!skipTurnstile) {
        token = await tryExecuteTurnstile()
        if (!token) {
          bypassed = true
          setVerificationBypassed(true)
        }
      }
      let submitResult
      if (onSubmit) {
        submitResult = await onSubmit({
          ...formValues,
          formCSRF,
          recaptchaToken: token || undefined,
          recaptcha: token || undefined,
          turnstileToken: token || undefined,
          turnstileBypassed: bypassed || undefined,
          form_action: formId || 'custom_form_submit',
        })
      }
      if (submitResult?.fieldErrors) {
        setFieldErrors(submitResult.fieldErrors)
      }
      if (submitResult?.errorMessage) {
        setErrorMessage(submitResult.errorMessage)
        return
      }
      setErrorMessage('')
      setFieldErrors({})
      setFormSubmitted(true)
      setThanksMessage(customThanksMessage || submitResult?.thanksMessage || 'Thank you for your submission.')
    } catch (error) {
      setErrorMessage(error?.message || 'There was an error submitting the form. Please try again.')
    } finally {
      setIsSubmitting(false)
      setResetCaptchaFlag((v) => !v)
      setRecaptchaValue('')
    }
  }, [clearErrors, customThanksMessage, formValues, onSubmit, recaptchaValue, validateAllFields, formCSRF, formId, skipTurnstile, tryExecuteTurnstile])

  const hasEmptyRequired = useMemo(() => {
    return memoizedFormFields.some((field) => {
      const required = Boolean(field?.props?.required)
      if (!required) return false
      const key = field?.props?.id || field?.props?.name
      if (!key) return false
      const value = formValues[key]
      const trimmed = typeof value === 'string' ? value.trim() : value
      return trimmed === '' || trimmed === null || trimmed === undefined || trimmed === false
    })
  }, [memoizedFormFields, formValues])

  const isDisabled = isSubmitting
  const disabledReason = ''

  const memoizedButtonProps = useMemo(() => {
    const baseSx = {
      backgroundColor: BLUE_BASE,
      border: 'none',
      fontSize: '14px',
      borderRadius: '25px',
      px: 2.5,
      py: 1.25,
      color: WHITE,
      mt: 2.5,
      mb: 5,
      textTransform: 'none',
    }

    const customSx = buttonStyles || {}
    const { ['&.Mui-disabled']: customDisabledStyles = {}, ...restCustomSx } = customSx
    const resolvedBackground = restCustomSx.backgroundColor || baseSx.backgroundColor

    return {
      type: 'submit',
      sx: {
        ...baseSx,
        ...restCustomSx,
        '&.Mui-disabled': {
          color: 'rgba(255, 255, 255, 0.9)',
          backgroundColor: resolvedBackground,
          opacity: 1,
          cursor: 'not-allowed',
          ...customDisabledStyles,
        },
      },
    }
  }, [buttonStyles])

  const errorSupportTypographySx = useMemo(() => ({ mt: 2.5, color: 'red' }), [])

  return (
    <FormContainer>
      {!formSubmitted ? (
        <FormBody onSubmit={handleSubmit} noValidate aria-label={formId || 'Custom Form'}>
          <FormRenderer
            formFields={memoizedFormFields}
            formValues={formValues}
            handleInputChange={handleInputChange}
            fieldErrors={fieldErrors}
            fieldRefs={fieldRefs}
            formHTML={formHTML}
            formStyles={formStyles}
          />
          {!skipTurnstile && (
            <RecaptchaWrapper
              ref={turnstileRef}
              formId={formId}
              gatsbyRecaptchaKey={process.env.GATSBY_RECAPTCHA_KEY || ''}
              onRecaptchaChange={handleRecaptchaChange}
              onRecaptchaExpire={handleRecaptchaExpire}
              formSubmitted={resetCaptchaFlag}
              size="invisible"
              theme="auto"
              action={formId || 'form_submit'}
            />
          )}
          {verificationBypassed && (
            <FallbackNotice role="status" aria-live="polite">
              We could not complete verification. Your submission will be reviewed.
            </FallbackNotice>
          )}
          {errorMessage && (
            <>
              {!suppressErrorDisplay && (
                <>
                  <ErrorDisplay errorMessage={errorMessage} fieldErrors={fieldErrors} />
                  <Typography sx={errorSupportTypographySx}>
                    Looking for additional support? <Link href="mailto:WebTeam@harborcapital.com">Click here</Link> to get in touch.
                  </Typography>
                </>
              )}
            </>
          )}
          <SubmitButtonWithStatus
            label={ctaText}
            loadingLabel="Submitting…"
            disabled={isDisabled}
            disabledReason={disabledReason}
            loading={isSubmitting}
            buttonProps={memoizedButtonProps}
          />
        </FormBody>
      ) : (
        <FormSuccess role="status" aria-live="polite">{thanksMessage}</FormSuccess>
      )}
    </FormContainer>
  )
}

CustomForm.propTypes = {
  formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  formFields: PropTypes.array,
  formHTML: PropTypes.string,
  isModal: PropTypes.bool,
  ctaText: PropTypes.string,
  onSubmit: PropTypes.func,
  thanksMessage: PropTypes.string,
  formCSRF: PropTypes.string.isRequired,
  buttonStyles: PropTypes.any,
  formStyles: PropTypes.any,
  initialValues: PropTypes.object,
  onValuesChange: PropTypes.func,
  suppressErrorDisplay: PropTypes.bool,
}

export default CustomForm
